import React, { useEffect, useState } from "react";
import { CheckCircle2, MessageSquare, Search, Send, Wallet, XCircle } from "lucide-react";
import useRequest from "../../../components/hook/use-req";
import Pagination from "../../../components/pagination";
import ReuseableDrawer from "../../../components/resuable-drawer";
import { formatCurrency } from "../../../utils/function";
import {
  KEY_TYPE_OPTIONS,
  KEY_TYPE_STYLES,
  MESSAGE_STATUS_OPTIONS,
  MESSAGE_STATUS_STYLES,
  TransactionalMessage,
  TransactionalSummary,
  formatReportDate,
} from "../../global-admin-pages/reports/types";

// Marketing > Transactional Messages — GET /transactional-messages, the
// signed-in user's own org only (session-authenticated). Every filter is
// optional; empty ones are dropped rather than sent blank.

type Period = "All time" | "24h" | "7 days" | "30 days" | "90 days";
const PERIODS: Period[] = ["All time", "24h", "7 days", "30 days", "90 days"];
const PERIOD_HOURS: Record<Period, number> = { "All time": 0, "24h": 24, "7 days": 24 * 7, "30 days": 24 * 30, "90 days": 24 * 90 };

const PAGE_SIZE = 20;
const STATUS_FALLBACK = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
const inputCls =
  "h-10 px-3 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500";

interface ListState {
  messages: TransactionalMessage[];
  total: number;
  pages: number;
  summary?: TransactionalSummary;
  currency?: string;
}

// The list may come back bare ({data, total, pages, ...}) or nested under
// `messages` like the admin report — read either.
const normalize = (res: any): ListState => {
  const list = res?.messages && !Array.isArray(res.messages) ? res.messages : res;
  const messages: TransactionalMessage[] = Array.isArray(list?.data) ? list.data : Array.isArray(res?.messages) ? res.messages : [];
  const total = Number(list?.total ?? messages.length) || 0;
  return {
    messages,
    total,
    pages: Number(list?.pages) || Math.max(1, Math.ceil(total / PAGE_SIZE)),
    summary: res?.summary,
    currency: res?.summary?.currency || res?.currency,
  };
};

const formatCost = (cost: number | null | undefined, currency?: string) =>
  cost == null ? "—" : currency ? formatCurrency(cost, currency, "symbol") : cost.toFixed(3);

const StatusPill: React.FC<{ value: string; styles: Record<string, string>; className?: string }> = ({ value, styles, className = "capitalize" }) => (
  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${className} ${styles[value] || STATUS_FALLBACK}`}>{value}</span>
);

const StatCard: React.FC<{ icon: React.ReactNode; tint: string; label: string; value: React.ReactNode; sublabel?: string }> = ({ icon, tint, label, value, sublabel }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 flex items-center gap-3 sm:gap-4 min-w-0">
    <span className={`inline-flex items-center justify-center w-11 h-11 rounded-xl shrink-0 ${tint}`}>{icon}</span>
    <div className="min-w-0">
      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">{label}</p>
      <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 truncate">{value}</p>
      {sublabel && <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{sublabel}</p>}
    </div>
  </div>
);

const MessageDrawer: React.FC<{ message: TransactionalMessage | null; currency?: string; onClose: () => void }> = ({ message, currency, onClose }) => {
  const rows: Array<[string, React.ReactNode]> = message
    ? [
        ["To", message.to],
        ["Sender ID", message.sender_id],
        ["Status", <StatusPill value={message.status} styles={MESSAGE_STATUS_STYLES} />],
        ["Key Type", <StatusPill value={message.key_type} styles={KEY_TYPE_STYLES} className="uppercase" />],
        ["API Key", message.api_key_name || "—"],
        ["Batch ID", message.batch_id || "—"],
        ["Gateway Message ID", message.gateway_message_id || "—"],
        ["Error Code", message.err_code || "—"],
        ["Cost", formatCost(message.cost, currency)],
        ["Sent", formatReportDate(message.created_at)],
        ["Updated", formatReportDate(message.updated_at)],
      ]
    : [];

  return (
    <ReuseableDrawer isOpen={!!message} onClose={onClose} title="Message Details" initialWidth="36%">
      {message && (
        <div className="p-5 max-w-4xl mx-auto space-y-4">
          <div className="border border-gray-100 dark:border-gray-800 rounded-lg p-3.5">
            <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Message</p>
            <p className="text-sm text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">{message.message}</p>
          </div>
          <div className="space-y-2.5">
            {rows.map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
                <span className="text-gray-800 dark:text-gray-100 text-right break-all">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ReuseableDrawer>
  );
};

const TransactionalMessages: React.FC = () => {
  const [period, setPeriod] = useState<Period>("30 days");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [keyType, setKeyType] = useState("");
  const [batchId, setBatchId] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ListState>({ messages: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<TransactionalMessage | null>(null);

  const { makeRequest: getMessages } = useRequest("/transactional-messages", "GET");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change goes back to page 1.
  useEffect(() => {
    setPage(1);
  }, [period, debouncedSearch, status, keyType, batchId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const hours = PERIOD_HOURS[period];
      const params: Record<string, string | number> = { page, limit: PAGE_SIZE };
      if (hours) {
        params.date_from = new Date(Date.now() - hours * 3600 * 1000).toISOString();
        params.date_to = new Date().toISOString();
      }
      if (status) params.status = status;
      if (keyType) params.key_type = keyType;
      if (debouncedSearch) params.search = debouncedSearch;
      if (batchId.trim()) params.batch_id = batchId.trim();
      const [res, code] = await getMessages(undefined, params);
      if (cancelled) return;
      if (code >= 400 || !res) setError(res?.detail && typeof res.detail === "string" ? res.detail : "Couldn't load transactional messages.");
      else setData(normalize(res));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, period, debouncedSearch, status, keyType, batchId]);

  const { messages, total, pages, summary, currency } = data;
  const hasFilters = !!(search || status || keyType || batchId);
  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setKeyType("");
    setBatchId("");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
              <Send className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Transactional Messages</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Messages sent from your API keys: OTPs, alerts and receipts</p>
            </div>
          </div>
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 overflow-x-auto self-start lg:self-auto max-w-full">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                  period === p ? "bg-blue-600 text-white" : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            icon={<MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
            tint="bg-blue-50 dark:bg-blue-500/15"
            label="Total Messages"
            value={(summary?.total ?? total).toLocaleString()}
            sublabel={summary ? `${summary.live_count.toLocaleString()} live · ${summary.test_count.toLocaleString()} test` : period === "All time" ? "All time" : `Last ${period}`}
          />
          <StatCard
            icon={<CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />}
            tint="bg-emerald-50 dark:bg-emerald-500/15"
            label="Delivered"
            value={summary ? summary.delivered.toLocaleString() : "—"}
            sublabel={summary ? `${summary.delivered_rate}% delivery rate` : undefined}
          />
          <StatCard
            icon={<XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />}
            tint="bg-rose-50 dark:bg-rose-500/15"
            label="Failed"
            value={summary ? summary.failed.toLocaleString() : "—"}
            sublabel={summary ? `${summary.failed_rate}% failure rate` : undefined}
          />
          <StatCard
            icon={<Wallet className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />}
            tint="bg-indigo-50 dark:bg-indigo-500/15"
            label="Total Cost"
            value={summary ? formatCost(summary.total_cost, currency) : "—"}
          />
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-3 border-b border-gray-100 dark:border-gray-800">
            <div className="relative w-full md:w-72">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search phone number or message" className={`${inputCls} w-full pl-9`} />
            </div>
            <div className="grid grid-cols-2 md:flex gap-3 md:ml-auto">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} capitalize`}>
                <option value="">All statuses</option>
                {MESSAGE_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <select value={keyType} onChange={(e) => setKeyType(e.target.value)} className={inputCls}>
                <option value="">Live &amp; test</option>
                {KEY_TYPE_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {k === "live" ? "Live keys" : "Test keys"}
                  </option>
                ))}
              </select>
              <input value={batchId} onChange={(e) => setBatchId(e.target.value)} placeholder="Batch ID" className={`${inputCls} col-span-2 md:w-40`} />
            </div>
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap self-start md:self-auto">
                Clear filters
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40">
                  {["To", "Sender ID", "Message", "Status", "Key", "Cost", "Sent"].map((h) => (
                    <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-14">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">Loading messages…</span>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={7} className="py-14 text-center text-sm text-rose-600 dark:text-rose-400">
                      {error}
                    </td>
                  </tr>
                ) : messages.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-14 text-center">
                      <MessageSquare className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No transactional messages</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {hasFilters || period !== "All time" ? "Try a wider date range or clear the filters." : "Messages sent through your API keys will show up here."}
                      </p>
                    </td>
                  </tr>
                ) : (
                  messages.map((m) => (
                    <tr
                      key={m.message_id}
                      onClick={() => setSelected(m)}
                      className="border-b border-gray-50 dark:border-gray-800 last:border-b-0 hover:bg-blue-50/40 dark:hover:bg-blue-500/10 transition-colors cursor-pointer"
                    >
                      <td className="py-3 px-4 text-gray-900 dark:text-gray-100 whitespace-nowrap">{m.to}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300 whitespace-nowrap">{m.sender_id}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300 truncate max-w-[260px]">{m.message}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <StatusPill value={m.status} styles={MESSAGE_STATUS_STYLES} />
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <StatusPill value={m.key_type} styles={KEY_TYPE_STYLES} className="uppercase" />
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatCost(m.cost, currency)}</td>
                      <td className="py-3 px-4 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatReportDate(m.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && !error && messages.length > 0 && (
            <div className="px-4 sm:px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + messages.length} of {total.toLocaleString()}
              </p>
              {pages > 1 && <Pagination currentPage={page} totalPages={pages} pageSize={PAGE_SIZE} isLoading={loading} onPageChange={setPage} />}
            </div>
          )}
        </div>
      </div>

      <MessageDrawer message={selected} currency={currency} onClose={() => setSelected(null)} />
    </div>
  );
};

export default TransactionalMessages;
